import React, { PropTypes, PureComponent } from 'react';
import { NOTIFICATION_CONTEXT_TYPES } from '../../../NotificationSystemProvider';
import EmptyState from '../../../common/components/EmptyState';
import { Button, LoadingWrapper } from 'react-fast-ui';
import TourneyDataTree, { TYPES } from '../../../components/TourneyDataTree';
import _ from 'underscore';
import {
  CourtModal,
  GroupModal,
  NameModal,
  TeamModal,
  VenueModal
} from '../../../components/modals/TourneyStructureEditModals';
import PageTitle from '../../../PageTitle';
import { Roles } from '../../../common/ApplicationModel';
import { fromUrlData, toUrlData } from '../../../util/UrlParams';
import ConfirmButton from '../../../components/buttons/ConfirmButton';
import ViewNotificationsModal, { SCOPES } from '../../../components/modals/ViewNotificationsModal';
import SuperAdminFeature from '../../../common/access/SuperAdminFeature';
import AdminFeature from '../../../common/access/AdminFeature';

const EDITING_NONE = _.mapObject(TYPES, () => null);

// maps the types to their respective modals
const Modals = {
  [ TYPES.division ]: NameModal,
  [ TYPES.group ]: GroupModal,
  [ TYPES.venue ]: VenueModal,
  [ TYPES.team ]: TeamModal,
  [ TYPES.court ]: CourtModal
};

const TYPE_NOTIFICATION_SCOPE = {
  [ TYPES.division ]: SCOPES.division,
  [ TYPES.group ]: SCOPES.pool,
  [ TYPES.team ]: SCOPES.team
};

// maps the types object to keys on our dao
const DAO_KEYS = {
  [ TYPES.division ]: 'divisions',
  [ TYPES.group ]: 'gameGroups',
  [ TYPES.venue ]: 'venues',
  [ TYPES.team ]: 'tourneyTeams',
  [ TYPES.court ]: 'courts'
};

export default class AdminTourneyStructure extends PureComponent {
  static contextTypes = {
    dao: PropTypes.object.isRequired,
    model: PropTypes.object.isRequired,
    router: PropTypes.object.isRequired,
    ...NOTIFICATION_CONTEXT_TYPES
  };

  static propTypes = {
    params: PropTypes.shape({ tourneyId: PropTypes.string.isRequired, data: PropTypes.string }),
    tourney: PropTypes.object
  };

  static defaultProps = {};

  state = {
    data: null,
    promise: null,

    editing: EDITING_NONE,
    viewingNotifications: null,

    expandedKeys: fromUrlData(this.props.params.data)
  };

  componentDidMount() {
    this.loadData(this.props.tourney);
  }

  componentWillReceiveProps({ tourney, params: { data } }) {
    if (tourney.id !== this.props.tourney.id) {
      this.loadData(tourney);
    }

    if (data !== this.props.params.data) {
      this.setState({ expandedKeys: fromUrlData(data) });
    }
  }

  refreshData = () => this.loadData(this.props.tourney, { removeData: false });

  loadData = (tourney, { removeData } = { removeData: true }) => {
    const { promise } = this.state;
    const { dao, onError } = this.context;

    if (!!promise) {
      promise.cancel();
    }

    const { id: tourneyId } = tourney;

    this.setState({
      ...(
        removeData ? { data: null, editing: EDITING_NONE } : null
      ),
      promise: Promise.all([
        dao.tourneyTeams.getAll({ tourneyId, sort: [ 'A|name' ] }),
        dao.divisions.getAll({ tourneyId, sort: [ 'A|name' ] }),
        dao.venues.getAll({ tourneyId, sort: [ 'A|name' ] }),
        dao.courts.getAll({ tourneyId, sort: [ 'A|name' ] }),
        dao.gameGroups.getAll({ tourneyId, sort: [ 'A|name' ] })
      ])
        .then(([ teams, divisions, venues, courts, groups ]) => {
          this.setState({
            data: {
              [ TYPES.team ]: teams,
              [ TYPES.division ]: divisions,
              [ TYPES.venue ]: venues,
              [ TYPES.court ]: courts,
              [ TYPES.group ]: groups
            }
          });
        })
        .catch(onError)
        .finally(
          () => this.setState({ promise: null })
        )
    });
  };

  handleDelete = (type, { id, name }) => {
    const { dao, onError, onSuccess } = this.context;

    const crud = dao[ DAO_KEYS[ type ] ];

    this.setState({
      promise: crud.destroyId(id)
        .then(
          () => {
            const { data } = this.state;
            const withoutId = arr => _.filter(arr, ({ id: inArr }) => inArr !== id);

            onSuccess(`Successfully deleted ${type}: ${name}`);

            this.setState({
              data: _.mapObject(data, withoutId)
            });
          }
        )
        .catch(err => onError(`Failed to delete ${type} with ID ${id}: ${err.message}`))
        .finally(
          () => this.setState({ promise: null })
        )
    });
  };

  handleChange = (type, value) => this.setState({ editing: { ...EDITING_NONE, [ type ]: value } });

  cancelEdit = () => this.setState({ editing: EDITING_NONE });

  handleSave = type => {
    const { dao, onError, onSuccess } = this.context;
    const { editing } = this.state;

    const crud = dao[ DAO_KEYS[ type ] ];

    this.setState({
      promise: crud.save(editing[ type ])
        .then(saved => onSuccess(`Successfully saved ${type}: ${saved.name}`))
        .then(() => this.setState({ editing: EDITING_NONE }))
        .catch(err => onError(`Failed to save ${type}: ${err.message}`))
        .finally(this.refreshData)
    });
  };

  handleCalculateStandings = groups => {
    const { dao, onError, onSuccess } = this.context;

    this.setState({
      promise: dao.calculatePoolStandings({ poolIds: _.pluck(groups, 'id') })
        .then(
          teams => {
            const poolNames = _.map(groups, ({ name, division: { name: dn } }) => `${dn}: ${name}`).join('; ');
            onSuccess(`Calculated standings for pool(s): ${poolNames}`);
            const { data } = this.state;
            const updatesById = _.indexBy(teams, 'id');

            this.setState({
              data: {
                ...data,
                [ TYPES.team ]: _.map(data[ TYPES.team ], team => updatesById[ team.id ] || team)
              }
            });
          }
        )
        .catch(onError)
        .finally(() => this.setState({ promise: null }))
    });
  };

  calculateAllStandings = () => {
    const { data: { [ TYPES.group ]: groups } } = this.state;

    this.handleCalculateStandings(_.filter(groups, ({ type }) => type === 'Pool'));
  };

  handleExpandedKeysChange = expandedKeys => {
    const { router } = this.context;
    const { params: { tourneyId } } = this.props;

    router.replace(`tourneys/${tourneyId}/structure/${toUrlData(expandedKeys)}`);
  };

  collapseAll = () => this.handleExpandedKeysChange({});
  expandAll = () => {
    const { data } = this.state;

    const {
      [ TYPES.division ]: divisions,
      [ TYPES.venue ]: venues,
      [ TYPES.group ]: groups
    } = data;

    const ids = _.chain([ divisions, venues, groups ])
      .flatten()
      .pluck('id')
      .map(id => [ id, true ])
      .object()
      .value();

    this.handleExpandedKeysChange(ids);
  };

  prependDivisionNames = () => {
    const { data: { [ TYPES.group ]: groups }, promise } = this.state;
    const { dao, onError } = this.context;

    if (!!promise) {
      return;
    }

    this.setState({
      promise: Promise.all(
        _.chain(groups)
          .filter(({ type }) => type === 'Bracket')
          .map(({ name, division, ...rest }) => ({ name: `${division.name} ${name}`, division, ...rest }))
          .map(bracket => dao.gameGroups.save(bracket))
          .value()
      )
        .catch(onError)
        .finally(this.refreshData)
    });
  };

  handleViewNotifications = (type, object) => {
    if (TYPE_NOTIFICATION_SCOPE[ type ]) {
      this.setState({ viewingNotifications: { scope: TYPE_NOTIFICATION_SCOPE[ type ], object } });
    }
  };
  closeViewingNotifications = () => this.setState({ viewingNotifications: null });

  render() {
    const { data, promise, editing, expandedKeys, viewingNotifications } = this.state;
    const { tourney } = this.props;
    const { model } = this.context;

    if (data === null) {
      if (promise === null) {
        return <EmptyState>No data!</EmptyState>;
      } else {
        return EmptyState.LOADING;
      }
    }

    return (
      <div className="mg-bottom-sm">
        <PageTitle title={`Structure | ${tourney.name}`}>
          <div>
            {
              _.map(TYPES, type => {
                const ModalComponent = Modals[ type ];
                const value = editing[ type ];

                return (
                  <ModalComponent
                    data={data}
                    key={type}
                    value={value}
                    title={`${value && value.id ? 'Editing' : 'Adding'} ${type}`}
                    open={value}
                    onClose={this.cancelEdit}
                    onChange={value => this.handleChange(type, value)}
                    onSave={() => this.handleSave(type)}/>
                );
              })
            }

            <SuperAdminFeature>
              <ViewNotificationsModal
                viewing={viewingNotifications}
                open={viewingNotifications}
                onClose={this.closeViewingNotifications}
              />
            </SuperAdminFeature>
          </div>
        </PageTitle>

        <div className="text-center">
          <AdminFeature>
            <ConfirmButton
              text="Calculate Standings"
              className="mg-sm"
              icon="sort-numeric-asc"
              size="sm"
              onConfirm={this.calculateAllStandings}
            />
          </AdminFeature>
          <Button text="Collapse All" icon="compress" className="mg-sm" onClick={this.collapseAll} size="sm"/>
          <Button text="Expand All" icon="expand" className="mg-sm" onClick={this.expandAll} size="sm"/>
          <AdminFeature>
            <ConfirmButton
              text="Prepend Division Name"
              className="mg-sm"
              icon="clipboard"
              size="sm" onConfirm={this.prependDivisionNames}
            />
          </AdminFeature>
        </div>

        <LoadingWrapper loading={!!promise}>
          <div className="pd-lg max-width" style={{ border: '1px solid aliceblue', fontSize: '1.4em' }}>
            <TourneyDataTree
              actionable={AdminFeature.canAccess(model)}
              onViewNotifications={SuperAdminFeature.canAccess(model) ? this.handleViewNotifications : null}
              tourney={tourney}
              data={data}
              onDelete={this.handleDelete}
              onEdit={this.handleChange}
              onCalculateStandings={this.handleCalculateStandings}
              onExpandedKeysChange={this.handleExpandedKeysChange}
              expandedKeys={expandedKeys || {}}
            />
          </div>
        </LoadingWrapper>
      </div>
    );
  }
}


// WEBPACK FOOTER //
// ./src/js/GameDay/components/pages/AdminTourneyStructure.jsx